import {
  msalInstance,
  LOGIN_SCOPES,
  bootstrapMsal,
  isMobileDevice,
  acquireTokenInteractive,
  loginWithMicrosoft,
  cleanAuthRedirectFromUrl,
} from './msalConfig';

const ensureMsalReady = async () => {
  await bootstrapMsal();
};

/**
 * Get the current access token for API calls
 * Only supports real Azure AD tokens - forces proper authentication
 */
export const getAccessToken = async () => {
  try {
    await ensureMsalReady();

    const demoToken = localStorage.getItem('demo-token');
    if (demoToken) {
      return demoToken;
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error('No authenticated user found. Please sign in with Microsoft.');
    }

    const account = msalInstance.getActiveAccount() || accounts[0];
    const tokenRequest = {
      scopes: LOGIN_SCOPES,
      account,
    };

    try {
      const response = await msalInstance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (error) {
      console.warn('Silent token acquisition failed, trying interactive:', error);
      const response = await acquireTokenInteractive(tokenRequest);
      if (!response) {
        throw new Error('Redirecting to sign in...');
      }
      return response.accessToken;
    }
  } catch (error) {
    console.error('Error getting access token:', error);
    throw error;
  }
};

/**
 * Get authorization headers for API calls
 */
export const getAuthHeaders = async () => {
  try {
    const token = await getAccessToken();
    return {
      Authorization: `Bearer ${token}`,
    };
  } catch (error) {
    console.error('Failed to get auth headers:', error);
    throw error;
  }
};

export const isDemoMode = () => !!localStorage.getItem('demo-token');

export const isAuthenticated = () => {
  const demoToken = localStorage.getItem('demo-token');
  if (demoToken) return true;

  try {
    return msalInstance.getAllAccounts().length > 0;
  } catch (error) {
    console.warn('MSAL not initialized when checking authentication:', error);
    return false;
  }
};

export const ensureAuthenticated = async () => {
  await ensureMsalReady();

  if (!isAuthenticated()) {
    try {
      await loginWithMicrosoft(msalInstance);
    } catch (error) {
      console.error('Login failed:', error);
      throw new Error('Authentication required. Please sign in with Microsoft.');
    }
  }
};

/**
 * Handle redirect response after Azure AD returns to the app.
 */
export const handleRedirectResponse = async () => {
  try {
    await ensureMsalReady();
    const response = await msalInstance.handleRedirectPromise();
    if (response?.account) {
      msalInstance.setActiveAccount(response.account);
      cleanAuthRedirectFromUrl();
      return response;
    }
  } catch (error) {
    console.error('Error handling redirect response:', error);
    if (error.errorCode) {
      console.error('Error code:', error.errorCode);
      console.error('Error description:', error.errorDesc);
    }
  }
  return null;
};

export { isMobileDevice, loginWithMicrosoft, logoutWithMicrosoft } from './msalConfig';
